import { useCallback, useEffect, useState } from 'react';
import { Delete } from 'lucide-react';
import { cn } from './cn.js';

/**
 * Touch-first PIN pad (4–6 digits). Physical keyboards work too (digits, Backspace, Enter).
 * Calls onSubmit(pin) on Enter/OK, or automatically when `maxLength` digits are entered.
 * To clear the pad (e.g. after a wrong PIN), change its React `key`.
 */
export function PinPad({ onSubmit, minLength = 4, maxLength = 6, disabled = false, error }) {
  const [pin, setPin] = useState('');

  const submit = useCallback(
    (value) => {
      if (value.length >= minLength && !disabled) onSubmit(value);
    },
    [minLength, disabled, onSubmit],
  );

  const press = useCallback(
    (d) => {
      if (disabled) return;
      setPin((p) => {
        if (p.length >= maxLength) return p;
        const next = p + d;
        if (next.length === maxLength) setTimeout(() => submit(next), 0);
        return next;
      });
    },
    [disabled, maxLength, submit],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Enter') submit(pin);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press, submit, pin]);

  const key =
    'flex h-16 items-center justify-center rounded-2xl bg-white text-2xl font-semibold text-slate-800 shadow-xs ring-1 ring-slate-200 active:bg-slate-100 disabled:opacity-50 select-none';
  return (
    <div className="mx-auto w-full max-w-xs">
      <div className="mb-5 flex justify-center gap-3" aria-label={`${pin.length} digits entered`}>
        {Array.from({ length: maxLength }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'size-4 rounded-full ring-2 transition',
              i < pin.length ? 'bg-brand-600 ring-brand-600' : 'ring-slate-300',
              error && 'ring-red-400',
            )}
          />
        ))}
      </div>
      {error && (
        <p className="mb-3 text-center text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" className={key} onClick={() => press(d)} disabled={disabled}>
            {d}
          </button>
        ))}
        <button
          type="button"
          className={cn(key, 'text-base')}
          onClick={() => setPin((p) => p.slice(0, -1))}
          disabled={disabled}
          aria-label="Delete"
        >
          <Delete className="size-6" />
        </button>
        <button type="button" className={key} onClick={() => press('0')} disabled={disabled}>
          0
        </button>
        <button
          type="button"
          className={cn(key, 'bg-brand-600 text-base text-white ring-brand-600 active:bg-brand-700')}
          onClick={() => submit(pin)}
          disabled={disabled || pin.length < minLength}
        >
          OK
        </button>
      </div>
    </div>
  );
}
